import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, Grid2, LinearProgress, MenuItem, TextField, ToggleButton, ToggleButtonGroup,
  Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SportsKabaddiOutlinedIcon from "@mui/icons-material/SportsKabaddiOutlined";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";

import { AuthContext } from "../../components/AuthContext";
import { ChangeChip, KpiCard, ListDrawer, Panel, sectionState, useSurface } from "../hpc/overview/OverviewUi";
import { AttendanceTrend } from "../hpc/overview/OverviewCharts";
import {
  PALETTE, RANGE_PRESETS, compareLabel, customRangeError, formatLkr, longDate, longMonth, periodLabel, plural,
  rangeFromParams, rangeText, requestError, shortDate, sriLankaToday, weekdayDate,
} from "../hpc/overview/overviewUtils";
import {
  ATTENDANCE_METHOD_META, INACTIVE_BUCKET_META, PAYMENT_METHOD_META, methodLabel, tebaOverviewApi,
} from "./tebaOverviewApi";
import {
  AlertsPanel, BarList, CollectedChart, FamiliesPanel, FirstPageList, InactiveBuckets, KeyDonut, MemberStatusPanel,
  OutstandingPanel,
} from "./TebaOverviewPanels";

const REFRESH_MS = 60000;
const isFinanceDrawer = (drawer) => drawer?.kind === "payments";

const checkInColumns = [
  { field: "date", headerName: "Date", flex: 0.8, minWidth: 110, valueGetter: (v) => longDate(v) },
  { field: "full_name", headerName: "Member", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
  { field: "membership_id", headerName: "Membership ID", flex: 1, minWidth: 130 },
  { field: "discipline", headerName: "Discipline", flex: 0.9, minWidth: 110, valueGetter: (v) => v || "-" },
  { field: "method", headerName: "Method", flex: 0.8, minWidth: 110, valueGetter: (v) => methodLabel(ATTENDANCE_METHOD_META, v) },
  { field: "branch_name", headerName: "Branch", flex: 0.8, minWidth: 100, valueGetter: (v) => v || "-" },
];
const paymentColumns = [
  { field: "payment_date", headerName: "Date", flex: 0.8, minWidth: 110, valueGetter: (v) => longDate(v) },
  { field: "receipt_number", headerName: "Receipt", flex: 1, minWidth: 150 },
  { field: "owner_name", headerName: "Family owner", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
  { field: "amount", headerName: "Amount", flex: 0.9, minWidth: 120, align: "right", headerAlign: "right",
    valueGetter: (v) => formatLkr(v, 2) },
  { field: "payment_method", headerName: "Method", flex: 0.8, minWidth: 110,
    valueGetter: (v) => methodLabel(PAYMENT_METHOD_META, v) },
  { field: "branch_name", headerName: "Branch", flex: 0.8, minWidth: 100, valueGetter: (v) => v || "-" },
];
const pendingColumns = (finance) => [
  { field: "full_name", headerName: "Member", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
  { field: "membership_id", headerName: "Membership ID", flex: 1, minWidth: 130 },
  { field: "subscription_name", headerName: "Subscription", flex: 1, minWidth: 120, valueGetter: (v) => v || "None" },
  { field: "latest_expiry", headerName: "Paid until", flex: 0.9, minWidth: 110,
    valueGetter: (v) => (v ? longDate(v) : "No payment") },
  ...(finance ? [{ field: "family_amount_due", headerName: "Family amount due", flex: 1, minWidth: 140,
    align: "right", headerAlign: "right", valueGetter: (v) => formatLkr(v, 2) }] : []),
];
const inactiveColumns = [
  { field: "full_name", headerName: "Member", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
  { field: "membership_id", headerName: "Membership ID", flex: 1, minWidth: 130 },
  { field: "last_check_in", headerName: "Last check-in", flex: 1, minWidth: 120, valueGetter: (v) => (v ? longDate(v) : "Never") },
  { field: "days_since", headerName: "Days since", flex: 0.7, minWidth: 100, valueGetter: (v) => (v == null ? "-" : plural(v, "day")) },
];

function FilterBar({ range, today, onPreset, onCustom, branchOptions, branch, onBranch }) {
  const s = useSurface();
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [error, setError] = useState("");
  const [showCustom, setShowCustom] = useState(range.preset === "custom");
  useEffect(() => { setFrom(range.from); setTo(range.to); }, [range.from, range.to]);

  const apply = () => {
    const problem = customRangeError(from, to, today);
    setError(problem);
    if (!problem) onCustom(from, to);
  };
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, mb: 2.5, borderRadius: 3, bgcolor: s.card, border: `1px solid ${s.border}` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <CalendarMonthOutlinedIcon sx={{ color: s.muted }} aria-hidden />
        <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
          <ToggleButtonGroup exclusive size="small" value={showCustom ? "custom" : range.preset} aria-label="Date range"
            onChange={(e, value) => {
              if (!value) return;
              if (value === "custom") { setShowCustom(true); return; }
              setShowCustom(false);
              setError("");
              onPreset(value);
            }}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 13, px: 1.5, whiteSpace: "nowrap" },
              "& .Mui-selected": { bgcolor: `${PALETTE.blue}33 !important`, color: `${s.text} !important`, fontWeight: 700 } }}>
            {RANGE_PRESETS.map((p) => <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
        {branchOptions && (
          <TextField select size="small" label="Branch" value={branch || "all"} onChange={(e) => onBranch(e.target.value)}
            sx={{ minWidth: 170, "& .MuiInputBase-input": { fontSize: 13 } }}>
            <MenuItem value="all">All branches</MenuItem>
            {branchOptions.map((b) => <MenuItem key={b.branch_id} value={String(b.branch_id)}>{b.branch_name}</MenuItem>)}
          </TextField>
        )}
        <Typography sx={{ fontSize: 13, color: s.muted, ml: { md: "auto" } }}>
          <Box component="span" sx={{ fontWeight: 700, color: s.text }}>{longDate(range.from)} – {longDate(range.to)}</Box>
        </Typography>
      </Box>
      {showCustom && (
        <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField type="date" size="small" label="From" value={from} onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: today }} sx={{ "& input": { fontSize: 13 } }} />
          <TextField type="date" size="small" label="To" value={to} onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: today }} sx={{ "& input": { fontSize: 13 } }} />
          <Button variant="contained" onClick={apply} sx={{ textTransform: "none" }}>Apply</Button>
          {error && <Alert severity="warning" variant="outlined" sx={{ py: 0, fontSize: 13 }}>{error}</Alert>}
        </Box>
      )}
    </Box>
  );
}

export default function TebaOverview() {
  const s = useSurface();
  const navigate = useNavigate();
  const { role } = useContext(AuthContext);
  const isGlobalAdmin = role === "global_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const today = sriLankaToday();
  const range = useMemo(() => rangeFromParams(searchParams, today), [searchParams, today]);
  // Branch filter (global admins only; branch admins are always scoped to their own branch by the server).
  const branch = isGlobalAdmin ? searchParams.get("branch") || "" : "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [drawer, setDrawer] = useState(null);
  const latestLoad = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++latestLoad.current;
    setLoading(true);
    try {
      const result = await tebaOverviewApi("", { from: range.from, to: range.to, preset: range.preset, branch_id: branch });
      if (requestId !== latestLoad.current) return;
      setData(result);
      setError(null);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      if (requestId !== latestLoad.current) return;
      setError(requestError(e, "Could not load the TEBA overview. Please try again."));
    } finally {
      if (requestId === latestLoad.current) setLoading(false);
    }
  }, [range.from, range.to, range.preset, branch]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const keepBranch = branch ? { branch } : {};
  const setPreset = (preset) => setSearchParams({ range: preset, ...keepBranch });
  const setCustom = (from, to) => setSearchParams({ range: "custom", from, to, ...keepBranch });
  const setBranch = (value) => {
    const next = Object.fromEntries(searchParams.entries());
    if (value === "all") delete next.branch; else next.branch = value;
    setSearchParams(next);
  };

  const failed = Boolean(error) && !data;
  const financeOn = data?.finance_visible === true;
  const shownRange = data?.range ? { from: data.range.from, to: data.range.to } : range;
  // Preset of the data on screen (echoed by the server) so titles never run ahead of the numbers.
  const kpiPreset = data?.range ? data.range.preset || "custom" : range.preset;
  const kpiPeriod = periodLabel(kpiPreset, shownRange.from, shownRange.to, data?.today || today);
  const todayMode = kpiPreset === "today";
  const monthMode = kpiPreset === "this_month";
  const kpis = data?.kpis || {};
  const attendance = kpis.attendance || {};
  const payments = kpis.payments || {};
  const listParams = { from: shownRange.from, to: shownRange.to, branch_id: branch };
  const openMember = (membershipId) => membershipId && navigate("/view_member", { state: { member: { membership_id: membershipId } } });
  const goReports = () => navigate("/generate_reports");
  const updatedAt = data?.as_of ? data.as_of.slice(11, 16) : null;

  useEffect(() => {
    if (!financeOn && isFinanceDrawer(drawer)) setDrawer(null);
  }, [financeOn, drawer]);

  const fetchPage = useCallback(async ({ page, pageSize, search }) => {
    if (!drawer || (!financeOn && isFinanceDrawer(drawer))) return { rows: [], total: 0 };
    const common = { branch_id: branch, q: search, page, page_size: pageSize };
    if (drawer.kind === "checkins") {
      const res = await tebaOverviewApi("/check-ins", { ...common, from: drawer.from, to: drawer.to });
      return { rows: res.items, total: res.total_count,
        summary: [plural(res.total_count, "check-in"), plural(res.unique_members, "member")] };
    }
    if (drawer.kind === "payments") {
      const res = await tebaOverviewApi("/payments", { ...common, from: drawer.from, to: drawer.to });
      return { rows: res.items, total: res.total_count,
        summary: [`Total ${formatLkr(res.total_amount, 2)}`, plural(res.total_count, "payment")] };
    }
    if (drawer.kind === "pending") {
      const res = await tebaOverviewApi("/payment-pending", common);
      return { rows: res.items, total: res.total_count, summary: [plural(res.total_count, "member")] };
    }
    if (drawer.kind === "inactive") {
      const res = await tebaOverviewApi("/inactive", { ...common, bucket: drawer.bucket });
      return { rows: res.items, total: res.total_count, summary: [plural(res.total_count, "member")] };
    }
    return { rows: [], total: 0 };
  }, [drawer, financeOn, branch]);

  const drawerConfig = useMemo(() => {
    if (!drawer || (!financeOn && isFinanceDrawer(drawer))) return null;
    switch (drawer.kind) {
      case "checkins":
        return { title: drawer.title || "Check-ins", subtitle: `Between ${rangeText(drawer.from, drawer.to)} · Sri Lanka time`,
          columns: checkInColumns, searchable: true, searchPlaceholder: "Name or membership ID", emptyText: "No check-ins" };
      case "payments":
        return { title: drawer.title || "Payments", subtitle: `Between ${rangeText(drawer.from, drawer.to)}`,
          columns: paymentColumns, searchable: true, searchPlaceholder: "Family owner or membership ID", emptyText: "No payments" };
      case "pending":
        return { title: "Payment pending", subtitle: "Members whose family payment has expired",
          columns: pendingColumns(financeOn), searchable: true, searchPlaceholder: "Name or membership ID",
          emptyText: "Nobody is payment pending" };
      case "inactive":
        return { title: `Not attending · ${INACTIVE_BUCKET_META[drawer.bucket].label}`, subtitle: "Members not set Inactive",
          columns: inactiveColumns, searchable: true, searchPlaceholder: "Name or membership ID", emptyText: "Nobody in this group" };
      default:
        return null;
    }
  }, [drawer, financeOn]);

  const kpiCount = 3 + (financeOn ? 1 : 0);
  const kpiWidth = { xs: 12, sm: 6, lg: 12 / kpiCount };

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }} sx={{ minWidth: 0, overflowX: "hidden" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2, mb: 2 }}>
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
          <Typography variant="h3" sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 800, color: s.text }}>TEBA Overview</Typography>
          <Typography sx={{ fontSize: 14, color: s.muted }}>
            {financeOn ? "Members, attendance and payments" : "Members and attendance"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, color: s.muted }}>
            {updatedAt ? `Last updated ${updatedAt} (${data.timezone || "Asia/Colombo"})` : "Loading…"}
          </Typography>
          <Button variant="outlined" onClick={load} disabled={loading} sx={{ textTransform: "none", fontSize: 13 }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshOutlinedIcon />}>
            Refresh
          </Button>
        </Box>
      </Box>

      <FilterBar range={range} today={today} onPreset={setPreset} onCustom={setCustom}
        branchOptions={isGlobalAdmin ? data?.branch_options || [] : null} branch={branch} onBranch={setBranch} />
      {loading && data && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={load} disabled={loading}>Retry</Button>}>
          {error}{data ? " Showing the last loaded data." : ""}
        </Alert>
      )}

      {/* KPIs */}
      <Grid2 container spacing={2} mb={2}>
        <Grid2 size={kpiWidth}>
          <KpiCard label="Active members" Icon={GroupOutlinedIcon} color={PALETTE.green}
            state={sectionState(kpis.active_members, failed)} reason={kpis.active_members?.reason}
            value={kpis.active_members?.value?.toLocaleString()} caption="Right now" />
        </Grid2>
        <Grid2 size={kpiWidth}>
          <KpiCard label="Payment pending" Icon={WarningAmberOutlinedIcon} color={PALETTE.amber}
            state={sectionState(kpis.payment_pending, failed)} reason={kpis.payment_pending?.reason}
            value={kpis.payment_pending?.value?.toLocaleString()} caption="Family payment has expired"
            onClick={() => setDrawer({ kind: "pending" })} />
        </Grid2>
        <Grid2 size={kpiWidth}>
          <KpiCard label={todayMode ? "Today's attendance" : `Attendance · ${kpiPeriod}`} Icon={EventAvailableOutlinedIcon}
            color={PALETTE.blue} state={sectionState(kpis.attendance, failed)} reason={attendance.reason}
            value={attendance.members?.toLocaleString()}
            change={<ChangeChip pct={attendance.change_pct} label={compareLabel(kpiPreset, attendance)} />}
            caption={`${plural(attendance.check_ins || 0, "check-in")} ${todayMode ? "today" : "in period"}`}
            onClick={() => setDrawer({ kind: "checkins", from: shownRange.from, to: shownRange.to })} />
        </Grid2>
        {financeOn && (
          <Grid2 size={kpiWidth}>
            <KpiCard label={monthMode ? "Payments received this month" : `Payments received · ${kpiPeriod}`}
              Icon={PaymentsOutlinedIcon} color={PALETTE.purple} state={sectionState(kpis.payments, failed)}
              reason={payments.reason} value={formatLkr(payments.value)}
              change={<ChangeChip pct={payments.change_pct}
                label={monthMode ? "vs same days last month" : compareLabel(kpiPreset, payments)} />}
              caption={monthMode
                ? `${plural(payments.payments || 0, "payment")} since ${shortDate(payments.period_from)}`
                : `${plural(payments.payments || 0, "payment")} ${todayMode ? "today" : "in period"}`}
              onClick={() => setDrawer({ kind: "payments", from: shownRange.from, to: shownRange.to })} />
          </Grid2>
        )}
      </Grid2>

      {/* Finance */}
      {financeOn && (
        <Grid2 container spacing={2} mb={2}>
          <Grid2 size={{ xs: 12, lg: 8 }} sx={{ minWidth: 0 }}>
            <CollectedChart section={data?.collected_by_month} failed={failed} range={shownRange}
              onMonth={(bar) => setDrawer({ kind: "payments", from: bar.from, to: bar.to, title: `Payments in ${longMonth(bar.key)}` })} />
          </Grid2>
          <Grid2 size={{ xs: 12, lg: 4 }} sx={{ minWidth: 0 }}>
            <OutstandingPanel section={data?.outstanding} failed={failed} onReports={goReports} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6, lg: 4 }} sx={{ minWidth: 0 }}>
            <KeyDonut title="Revenue by method" subtitle={rangeText(shownRange.from, shownRange.to)} Icon={PaymentsOutlinedIcon}
              color={PALETTE.purple} section={data?.revenue_by_method} failed={failed} meta={PAYMENT_METHOD_META}
              valueKey="amount" format={(v) => formatLkr(v)} centerLabel="collected" emptyText="No payments in this period." />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6, lg: 4 }} sx={{ minWidth: 0 }}>
            <BarList title="Revenue by subscription" subtitle={rangeText(shownRange.from, shownRange.to)}
              Icon={SubscriptionsOutlinedIcon} color={PALETTE.purple} section={data?.revenue_by_subscription} failed={failed}
              valueKey="amount" format={(v) => formatLkr(v)} emptyText="No payments in this period." />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6, lg: 4 }} sx={{ minWidth: 0 }}>
            <BarList title="Revenue by branch" subtitle={rangeText(shownRange.from, shownRange.to)}
              Icon={StorefrontOutlinedIcon} color={PALETTE.purple} section={data?.revenue_by_branch} failed={failed}
              valueKey="amount" format={(v) => formatLkr(v)} emptyText="No payments in this period."
              info="By the family owner's branch, as in the TEBA payment reports." />
          </Grid2>
        </Grid2>
      )}

      {/* Attendance */}
      <Grid2 container spacing={2} mb={2}>
        <Grid2 size={{ xs: 12, lg: 6 }} sx={{ minWidth: 0 }}>
          <AttendanceTrend section={data?.attendance_trend} failed={failed} range={shownRange}
            onDay={(date) => setDrawer({ kind: "checkins", from: date, to: date, title: `Check-ins on ${weekdayDate(date)}` })} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
          <BarList title="Attendance by discipline" subtitle="Check-ins in the period" Icon={SportsKabaddiOutlinedIcon}
            color={PALETTE.blue} section={data?.attendance_by_discipline} failed={failed} valueKey="check_ins"
            emptyText="No check-ins in this period." />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
          <KeyDonut title="Check-in method" subtitle="Check-ins in the period" Icon={TouchAppOutlinedIcon} color={PALETTE.blue}
            section={data?.attendance_by_method} failed={failed} meta={ATTENDANCE_METHOD_META} valueKey="check_ins"
            format={(v) => v.toLocaleString()} centerLabel="check-ins" emptyText="No check-ins in this period." />
        </Grid2>
      </Grid2>

      {/* Membership (snapshots) */}
      <Grid2 container spacing={2} mb={2}>
        <Grid2 size={{ xs: 12, md: 6, lg: 4 }} sx={{ minWidth: 0 }}>
          <MemberStatusPanel section={data?.member_status} failed={failed} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: 4 }} sx={{ minWidth: 0 }}>
          <AlertsPanel alerts={data?.alerts} failed={failed} onPending={() => setDrawer({ kind: "pending" })} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: 4 }} sx={{ minWidth: 0 }}>
          <InactiveBuckets section={data?.inactive_members} failed={failed}
            onBucket={(bucket) => setDrawer({ kind: "inactive", bucket })} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
          <BarList title="Members by branch" subtitle="Right now" Icon={StorefrontOutlinedIcon} color={PALETTE.green}
            section={data?.members_by_branch} failed={failed} emptyText="No members." />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
          <BarList title="Members by discipline" subtitle="Right now" Icon={SportsKabaddiOutlinedIcon} color={PALETTE.green}
            section={data?.members_by_discipline} failed={failed} emptyText="No members."
            info="A member enrolled in two disciplines is counted in both." />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
          <BarList title="Members by subscription" subtitle="Right now" Icon={SubscriptionsOutlinedIcon} color={PALETTE.green}
            section={data?.members_by_subscription} failed={failed} emptyText="No members." />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
          <FamiliesPanel section={data?.families} failed={failed} />
        </Grid2>
      </Grid2>

      {/* Lists */}
      <Grid2 container spacing={2} mb={2}>
        <Grid2 size={{ xs: 12, xl: 6 }} sx={{ minWidth: 0 }}>
          <FirstPageList title="Recent check-ins" subtitle="Latest 10" Icon={EventAvailableOutlinedIcon} color={PALETTE.blue}
            path="/check-ins" params={listParams} refreshKey={refreshKey} columns={checkInColumns}
            emptyText={`No check-ins between ${rangeText(shownRange.from, shownRange.to)}.`}
            footer={(page) => plural(page?.total_count || 0, "check-in")}
            onRowClick={({ row }) => openMember(row.membership_id)}
            onViewAll={() => setDrawer({ kind: "checkins", from: shownRange.from, to: shownRange.to })} />
        </Grid2>
        <Grid2 size={{ xs: 12, xl: 6 }} sx={{ minWidth: 0 }}>
          <FirstPageList title="Payment pending" subtitle="Right now · first 10" Icon={WarningAmberOutlinedIcon}
            color={PALETTE.amber} path="/payment-pending" params={{ branch_id: branch }} refreshKey={refreshKey}
            columns={pendingColumns(financeOn)} getRowId={(row) => row.member_id} emptyText="Nobody is payment pending."
            footer={(page) => plural(page?.total_count || 0, "member")}
            onRowClick={({ row }) => openMember(row.membership_id)} onViewAll={() => setDrawer({ kind: "pending" })} />
        </Grid2>
        {financeOn && (
          <Grid2 size={12} sx={{ minWidth: 0 }}>
            <FirstPageList title="Recent payments" subtitle="Latest 10" Icon={ReceiptLongOutlinedIcon} color={PALETTE.purple}
              path="/payments" params={listParams} refreshKey={refreshKey} columns={paymentColumns}
              emptyText={`No payments between ${rangeText(shownRange.from, shownRange.to)}.`}
              footer={(page) => `${plural(page?.total_count || 0, "payment")} · total ${formatLkr(page?.total_amount, 2)}`}
              onRowClick={({ row }) => openMember(row.membership_id)}
              onViewAll={() => setDrawer({ kind: "payments", from: shownRange.from, to: shownRange.to })} />
          </Grid2>
        )}
        {financeOn && (
          <Grid2 size={12} sx={{ minWidth: 0 }}>
            <Panel icon={<AssessmentOutlinedIcon sx={{ color: PALETTE.purple }} />} title="Payment reports"
              subtitle="Monthly collection, date range, by discipline, detailed and outstanding fees (PDF)">
              <Button variant="contained" onClick={goReports} sx={{ textTransform: "none" }}>Open TEBA payment reports</Button>
            </Panel>
          </Grid2>
        )}
      </Grid2>

      <ListDrawer
        open={Boolean(drawer && drawerConfig)}
        onClose={() => setDrawer(null)}
        fetchPage={fetchPage}
        getRowId={(row) => row.id ?? row.member_id}
        onRowClick={({ row }) => openMember(row.membership_id)}
        {...(drawerConfig || { columns: [] })}
      />
    </Box>
  );
}

